import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {Mail, Lock, Music} from 'react-feather';
import { login } from '../services/api';
import { setAuthData } from '../services/auth';

interface LoginPageProps {
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginPage: React.FC<LoginPageProps> = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if there's a redirect path in the state
  const from = location.state?.from?.pathname || '/dashboard';

  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!credentials.email.trim()) {
      newErrors.email = t('login.errors.emailRequired');
    }

    if (!credentials.password) {
      newErrors.password = t('login.errors.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await login(credentials);

      // Store authentication data
      setAuthData(
        response.data.tokens.access_token,
        response.data.tokens.refresh_token,
        response.data.user
      );

      // Update authentication state
      setIsAuthenticated(true);

      // Redirect to dashboard or the page user was trying to access
      navigate(from, { replace: true });

    } catch (error: any) {
      if (error.response?.status === 401) {
        setError(t('login.errors.invalidCredentials') as string);
      } else {
        setError(error.response?.data?.error || t('login.errors.generalError'));
      }
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6} xl={5}>
          <div className="text-center mb-4">
            <img
                src="/logo.png"
                alt="MyWeeshy Logo"
                className="me-2"
                width={50}
                style={{ objectFit: "cover" }}
            />
            <h5 className="mb-3 d-inline align-middle">
              MyWeeshy
            </h5>
            <p className="text-muted">{t('login.subtitle')}</p>
          </div>

          <Card className="shadow-sm">
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('login.email')}</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <Mail size={18} />
                    </span>
                    <Form.Control
                      type="email"
                      name="email"
                      value={credentials.email}
                      onChange={handleChange}
                      placeholder={t('login.emailPlaceholder') as string}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label>{t('login.password')}</Form.Label>
                    <Link to="/forgot-password" className="small text-decoration-none" style={{ color: '#795EFF' }}>
                      {t('login.forgotPassword')}
                    </Link>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text">
                      <Lock size={18} />
                    </span>
                    <Form.Control
                      type="password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      placeholder={t('login.passwordPlaceholder') as string}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="remember-me"
                    label={t('login.rememberMe')}
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                </Form.Group>

                <div className="d-grid mb-3">
                  <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: '#795EFF',
                        borderColor: '#795EFF',
                        color: 'white'
                      }}
                  >
                    {loading ? (
                        <>
                          <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                          />
                          {t('loading')}
                        </>
                    ) : t('login.loginButton')}
                  </Button>

                </div>

                <div className="text-center">
                  <p className="mb-0">
                    {t('login.noAccount')}{' '}
                    <Link
                        to="/signup"
                        className="text-decoration-none"
                        style={{ color: '#795EFF' }}
                    >
                      {t('login.signupLink')}
                    </Link>
                  </p>
                </div>

              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
